import { z } from "zod";

export const WORKER_EVENTS_CHANNEL = "worker_events";

export const WORKER_JOB_EVENT_TYPES = ["worker.job_status_changed"] as const;
export type WorkerJobEventType = (typeof WORKER_JOB_EVENT_TYPES)[number];

export const WORKER_JOB_EVENT_STATUSES = ["queued", "processing", "done", "failed"] as const;
export type WorkerJobEventStatus = (typeof WORKER_JOB_EVENT_STATUSES)[number];

export const WORKER_JOB_TYPES = [
  "origin",
  "discovered",
  "fact",
  "visual_identity",
  "strategy_generation",
  "visual_universe_generation",
  "visual_universe_image_generation",
  "standalone_asset_batch_metadata",
  "social_post_generation",
  "social_post_copy_generation",
  "social_post_image_regeneration",
  "assistant_image_generation",
  "social_publish",
  "instagram_media_sync",
  "campaign_generation",
  "brand_asset_processing",
] as const;
export type WorkerJobEventJobType = (typeof WORKER_JOB_TYPES)[number];

const workerJobStatusChangedPayloadV1Schema = z.object({
  jobId: z.uuid(),
  jobType: z.enum(WORKER_JOB_TYPES),
  status: z.enum(WORKER_JOB_EVENT_STATUSES),
  brandId: z.uuid(),
  pageId: z.uuid().optional(),
  traceId: z.string().min(1),
  attemptCount: z.number().int().nonnegative(),
  maxAttempts: z.number().int().positive(),
  generationTaskId: z.uuid().optional(),
  campaignId: z.uuid().optional(),
  scheduledPostId: z.uuid().optional(),
  blockId: z.string().min(1).optional(),
  canonicalAssetId: z.uuid().optional(),
  error: z.string().min(1).optional(),
});

export type WorkerJobStatusChangedPayloadV1 = z.infer<typeof workerJobStatusChangedPayloadV1Schema>;

export const workerJobStatusChangedEnvelopeV1Schema = z.object({
  id: z.uuid(),
  type: z.literal("worker.job_status_changed"),
  version: z.literal(1),
  occurredAt: z.iso.datetime({ offset: true }),
  payload: workerJobStatusChangedPayloadV1Schema,
});

export type WorkerJobStatusChangedEnvelopeV1 = z.infer<
  typeof workerJobStatusChangedEnvelopeV1Schema
>;
export type WorkerRealtimeEventEnvelope = WorkerJobStatusChangedEnvelopeV1;

const workerRealtimeEventEnvelopeSchema = z.union([workerJobStatusChangedEnvelopeV1Schema]);

const EVENT_SCHEMA_BY_KEY: Record<string, z.ZodType<WorkerRealtimeEventEnvelope>> = {
  "worker.job_status_changed:1": workerJobStatusChangedEnvelopeV1Schema,
};

const buildEventSchemaKey = (type: string, version: number) => `${type}:${version}`;

export const createWorkerJobStatusChangedEventV1 = (
  payload: WorkerJobStatusChangedPayloadV1,
): WorkerJobStatusChangedEnvelopeV1 => {
  return workerJobStatusChangedEnvelopeV1Schema.parse({
    id: crypto.randomUUID(),
    type: "worker.job_status_changed",
    version: 1,
    occurredAt: new Date().toISOString(),
    payload,
  });
};

export const parseWorkerRealtimeEventEnvelope = (input: unknown): WorkerRealtimeEventEnvelope => {
  const envelope = z
    .object({
      type: z.string().min(1),
      version: z.number().int().positive(),
    })
    .loose()
    .parse(input);

  const schema = EVENT_SCHEMA_BY_KEY[buildEventSchemaKey(envelope.type, envelope.version)];
  if (!schema) {
    return workerRealtimeEventEnvelopeSchema.parse(input);
  }

  return schema.parse(input);
};
